import React, { useState } from 'react';
import { User as PersonIcon, Pencil } from 'lucide-react';

import { Access, Role } from 'src/shared/types';

import { SecondaryButton } from 'src/shared/components/button/Button';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';
import StatusIndicator from 'src/shared/components/status-indicator/StatusIndicator';
import InfoMessage from 'src/shared/components/info-message/InfoMessage';

import EditProjectUserHeader from 'src/app/admin/views/user-management/components/edit-project-user/components/EditProjectUserHeader';
import ProjectPermissionsMatrixView from 'src/app/admin/views/user-management/components/assign-projects/components/project-permissions-matrix/ProjectPermissionsMatrixView';
import UserManagementDrawerLayout from 'src/app/admin/views/user-management/components/user-drawer-layout/UserManagementDrawerLayout';
import { getUserStatusFromEnum } from 'src/app/admin/views/user-management/userManagementHelpers';

import useCustomer from 'src/app/customers/hooks/useCustomer';

import { useAppDispatch, useAppSelector } from 'src/store';
import ExpandableSection from 'src/shared/components/expandable-section/ExpandableSection';
import usePermissions from 'src/shared/hooks/usePermissions';
import { batch } from 'react-redux';
import styles from './ViewProjectUser.module.scss';
import { getEditProjectUserFormState } from '../../state/userManagementSelector';
import { setEditUserFormMode, setEditUserView, updateGeneralUiState } from '../../state/userManagementSlice';

/**
 * Read-only user modal (`shouldShowViewUserModal`). Uses the same `editProjectUserForm` slice for data.
 * Customer admins get **Edit user** (opens edit on default step) or **Assign to this organisation** when unassigned.
 */
export const ViewProjectUser = () => {
    const [errorMessage, setErrorMessage] = useState('');
    const [isOrgSectionOpen, setIsOrgSectionOpen] = useState(false);
    const { allCustomers, currentCustomerId } = useCustomer();
    const formState = useAppSelector(getEditProjectUserFormState);
    const isActiveOrInvited = formState.status === 'Active' || formState.status === 'Invited';
    const isAssignedToCustomer = formState.customers.some(customer => customer.customerId === currentCustomerId && customer.active);
    const { hasCustomerLevelRole } = usePermissions();
    const dispatch = useAppDispatch();

    const getCustomerName = (customerId: string) => {
        const customerDetail = allCustomers?.customers.find(customer => customer.customerId === customerId);
        return customerDetail?.customerName || '';
    };

    const activeStatusIndicator = <StatusIndicator status={getUserStatusFromEnum(formState.status)} statusText={formState.status} />;

    const otherOrgs = formState.customers.filter(customer => customer.customerId !== currentCustomerId);

    const expandedOrgDetailsContent = otherOrgs.length ? (
        <div className={styles.orgList}>
            {otherOrgs.map(otherOrg => (
                <div className={styles.orgDetail} key={otherOrg.customerId}>
                    <div className={styles.orgTitle}>{getCustomerName(otherOrg.customerId)}</div>
                    {activeStatusIndicator}
                </div>
            ))}
        </div>
    ) : (
        <InfoMessage message='This user is not assigned to any other organisations' />
    );

    const orgDetails = (
        <ExpandableSection
            isExpanded={isOrgSectionOpen}
            onToggle={setIsOrgSectionOpen}
            expandedContent={
                <>
                    <div className={styles.orgSectionTitle}>User&#8217;s other organisations</div>
                    {expandedOrgDetailsContent}
                </>
            }
            collapsedContent={<div>User&#8217;s other organisations</div>}
        />
    );

    const isCurrentUserCustomerAdmin = hasCustomerLevelRole({
        customerId: currentCustomerId,
        role: Role.CUSTOMER_ADMIN,
    });

    /** Show matrix whenever the user is assigned to this org — `ProjectPermissionsMatrixView` handles empty / loading states */
    const projectDetails =
        isAssignedToCustomer && currentCustomerId ? (
            <div className={styles.section}>
                <div className={styles.sectionHeading}>Site permissions</div>
                <ProjectPermissionsMatrixView
                    projects={formState.projects}
                    status={formState.status}
                    is_internal_user={formState.is_internal_user}
                    disabled={!isActiveOrInvited}
                    viewTargetUserId={formState.id}
                />
            </div>
        ) : null;

    const openEditUserDefault = () => {
        batch(() => {
            dispatch(updateGeneralUiState({ shouldShowViewUserModal: false, shouldShowEditUserModal: true }));
            dispatch(setEditUserFormMode(Access.UPDATE));
            dispatch(setEditUserView('default'));
        });
    };

    const openAssignToOrganisation = () => {
        batch(() => {
            dispatch(updateGeneralUiState({ shouldShowViewUserModal: false, shouldShowEditUserModal: true }));
            dispatch(setEditUserFormMode(Access.UPDATE));
            dispatch(setEditUserView('reassign'));
        });
    };

    const titleBarAction = isCurrentUserCustomerAdmin ? (
        isAssignedToCustomer ? (
            <SecondaryButton
                type='button'
                className={styles.titleBarButton}
                onClick={openEditUserDefault}
                data-testid='user-management-view-user-open-edit'
            >
                <Pencil className={styles.titleBarButtonIcon} aria-hidden />
                &nbsp;Edit user
            </SecondaryButton>
        ) : (
            <SecondaryButton
                type='button'
                className={styles.titleBarButton}
                onClick={openAssignToOrganisation}
                data-testid='user-management-view-user-assign-to-org'
            >
                <PersonIcon className={styles.titleBarButtonIcon} aria-hidden />
                &nbsp;Assign to this organisation
            </SecondaryButton>
        )
    ) : null;

    return (
        <div className={styles.viewProjectUser} data-testid='user-management-view-user-panel'>
            <div className={styles.viewTitleRow}>
                <div className={styles.title}>View user</div>
                {titleBarAction}
            </div>
            <UserManagementDrawerLayout
                variant='stacked'
                sidebar={
                    <div className={styles.popupSections}>
                        <EditProjectUserHeader setErrorMessage={setErrorMessage} hideEditLink />
                        {orgDetails}
                    </div>
                }
                main={projectDetails ? <div className={styles.popupSections}>{projectDetails}</div> : null}
            />
            <div>{errorMessage && <ErrorMessage message={errorMessage} />}</div>
        </div>
    );
};
